/**
 * Paywall — Solo vs Team. Staging checkout uses the matching Stripe price.
 */

import React, { useState } from 'react';
import { useSubscription } from '../../context/SubscriptionContext';
import { useAuth } from '../AuthProvider';
import { PERSONAL_WORKSPACE_ID } from '../../utils/api';
import { isWorkspaceCovered, normalizeWorkspaceRole } from '../../utils/workspaceAccess';
import LogoLoader from '../ui/LogoLoader';
import './Paywall.css';

const Paywall = ({ embedded = false }) => {
  const { logout, workspaces, setWorkspace, workspaceId } = useAuth();
  const { startCheckout, status, loading: subLoading } = useSubscription();
  const [selectedPlan, setSelectedPlan] = useState('solo');
  const [isLoading, setIsLoading] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState(null);

  const handleSubscribe = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await startCheckout(selectedPlan, workspaceId);
      if (result?.url && window.electronAPI?.openExternal) {
        setError(null);
        return;
      }
      if (!result?.url && !result?.switched) {
        setError('Failed to start checkout. Please try again.');
      }
    } catch (err) {
      setError('Failed to start checkout. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    setError(null);
    try {
      await logout();
    } catch (err) {
      console.error('Sign out failed:', err);
      setError('Could not sign out. Please try again.');
      setSigningOut(false);
    }
  };

  const namedRooms = (workspaces || []).filter(
    (ws) => ws?.id && ws.id !== PERSONAL_WORKSPACE_ID
  );
  const coveredRooms = namedRooms.filter(isWorkspaceCovered);
  const currentRoom = (workspaces || []).find((w) => w.id === workspaceId);
  const currentName = currentRoom?.name;
  const ownsThisRoom = normalizeWorkspaceRole(currentRoom?.role) === 'owner';
  const canPickPlan = ownsThisRoom || namedRooms.length === 0;

  const getMessage = () => {
    if (!currentRoom) {
      return {
        title: 'Your first brain',
        subtitle: 'Pick Solo for just you, or Team if you will invite people.',
      };
    }
    if (!ownsThisRoom) {
      return {
        title: currentName
          ? `${currentName} needs a plan`
          : 'This brain needs a plan',
        subtitle:
          'The owner of this brain needs to add a plan. You do not pay to work in someone else’s room.',
      };
    }
    switch (status) {
      case 'canceled':
        return {
          title: 'Your subscription has ended',
          subtitle: 'Resubscribe to keep this brain.',
        };
      case 'past_due':
        return {
          title: 'Payment failed',
          subtitle: 'Update your payment method to keep this brain.',
        };
      case 'unpaid':
        return {
          title: 'Payment required',
          subtitle: 'Complete payment to keep this brain.',
        };
      default:
        return {
          title: currentName
            ? `${currentName} needs a plan`
            : 'This brain needs a plan',
          subtitle:
            coveredRooms.length > 0
              ? 'You pay for each brain you own. A room you were invited to is already covered — switch back in the sidebar.'
              : 'Pick Solo for just you, or Team if you will invite people.',
        };
    }
  };

  const message = getMessage();

  return (
    <div className={`paywall ${embedded ? 'paywall--embedded' : ''}`}>
      <div className="paywall__container">
        <div className="paywall__logo">
          <LogoLoader size={embedded ? 64 : 80} />
        </div>

        {/* Header */}
        <div className="paywall__header">
          <h1 className="paywall__title">{message.title}</h1>
          <p className="paywall__subtitle">{message.subtitle}</p>
        </div>

        {canPickPlan && (
          <>
            <div className="paywall__plans">
              <button
                type="button"
                className={`paywall__plan ${selectedPlan === 'solo' ? 'paywall__plan--selected' : ''}`}
                onClick={() => setSelectedPlan('solo')}
              >
                <div className="paywall__plan-header">
                  <span className="paywall__plan-name">Solo</span>
                </div>
                <div className="paywall__plan-price">
                  <span className="paywall__plan-amount">$30</span>
                  <span className="paywall__plan-period">/month</span>
                </div>
                <div className="paywall__plan-details">
                  Just you. No invites.
                </div>
              </button>

              <button
                type="button"
                className={`paywall__plan ${selectedPlan === 'team' ? 'paywall__plan--selected' : ''}`}
                onClick={() => setSelectedPlan('team')}
              >
                <div className="paywall__plan-header">
                  <span className="paywall__plan-name">Team</span>
                </div>
                <div className="paywall__plan-price">
                  <span className="paywall__plan-amount">$40</span>
                  <span className="paywall__plan-period">/seat</span>
                </div>
                <div className="paywall__plan-details">
                  You plus people you invite
                </div>
              </button>
            </div>

            <button
              className="paywall__cta"
              onClick={handleSubscribe}
              disabled={isLoading || subLoading}
            >
              {isLoading ? (
                <span className="paywall__cta-loading">
                  <span className="paywall__spinner"></span>
                  Redirecting to checkout...
                </span>
              ) : (
                'Continue to checkout'
              )}
            </button>

            {error && (
              <div className="paywall__error">
                {error}
              </div>
            )}

            <div className="paywall__features">
              <h3 className="paywall__features-title">
                {selectedPlan === 'team' ? 'Team' : 'Solo'}
              </h3>
              <ul className="paywall__features-list">
                <li>
                  <span className="paywall__feature-icon">✓</span>
                  Campaigns, docs, and the room
                </li>
                <li>
                  <span className="paywall__feature-icon">✓</span>
                  {selectedPlan === 'team'
                    ? 'Invite people — one seat each'
                    : 'Just you — switch to Team to invite'}
                </li>
              </ul>
            </div>

            <p className="paywall__fine-print">
              You will be charged today. Cancel anytime in your account settings.
            </p>
          </>
        )}

        {coveredRooms.length > 0 && (
          <div className="paywall__back">
            <p className="paywall__back-hint">
              Or go back to a brain that is already covered.
            </p>
            {coveredRooms.map((ws) => (
              <button
                key={ws.id}
                type="button"
                className="paywall__back-btn"
                onClick={() => setWorkspace(ws.id)}
                disabled={isLoading || signingOut}
              >
                Back to {ws.name || 'brain'}
              </button>
            ))}
          </div>
        )}

        <div className="paywall__footer-actions">
          <button
            type="button"
            className="paywall__sign-out"
            onClick={handleSignOut}
            disabled={signingOut || isLoading}
          >
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Paywall;

